import { Base, Container, Parse, Section } from '@/components/Core'
import { routing } from '@/i18n/routing'
import { Lib } from '@/types'
import clsx from 'clsx'

import dynamic from 'next/dynamic'
import { memo } from 'react'
import SectionHeroFragment from './query'

import HeaderTrigger from '@/components/Project/Header/components/HeaderTrigger'

const GradientBg = dynamic(() => import('@/components/Layout/GradientBg'), {
  ssr: false
})

const Image = dynamic(() => import('@/components/Core/Image'))

export interface SectionHeroProps {
  data: Lib.FragmentOf<typeof SectionHeroFragment>
  params?: {
    locale: (typeof routing.locales)[number]
    slug: string[]
  }
}

const SectionHero = ({ data }: SectionHeroProps) => {
  const { variants, text, title, image, _modelApiKey, background } = data

  return (
    <Section
      as="div"
      className={clsx(
        'relative overflow-hidden',
        background === 'gray' && 'bg-neutral-300'
      )}
      sectionName={_modelApiKey}
      mainWrapper={false}
      anim="section-hero"
    >
      <HeaderTrigger />
      <div
        className={clsx(
          'main-wrapper pt-vertical-large pb-vertical-small md:pb-vertical-main relative z-10 flex items-end overflow-hidden rounded-br-[20vw] text-white md:rounded-br-[10rem]',
          variants === 'default' && 'min-h-[clamp(26rem,40vw,35rem)]',
          variants === 'home' && 'h-[80vh] md:h-[95vh]'
        )}
      >
        {image ? (
          <Image
            imgClassName={'absolute h-full w-full object-cover'}
            fitWrap
            isClient={true}
            data={image}
          />
        ) : (
          <GradientBg />
        )}

        {variants === 'home' && (
          <svg
            preserveAspectRatio="xMaxYMid meet"
            viewBox="0 0 658 520"
            className="pointer-events-none absolute bottom-0 z-10 w-[150%] fill-none stroke-0 opacity-10 max-md:left-0 md:right-0 md:max-h-3/4 md:w-full"
          >
            <path
              d="M464.281 0.104858H276.901V544.413H356.966V381.937H464.281C637.193 381.937 676.515 305.091 676.515 190.622C676.515 76.1518 637.193 0.104858 464.281 0.104858ZM356.434 382.026H278.144V309.972H356.434V382.026ZM464.281 309.883H356.966V72.1587H464.281C573.194 72.1587 596.45 100.199 596.45 190.622C596.45 281.044 573.194 309.883 464.281 309.883Z"
              fill="white"
            />
            <path
              d="M276.634 309.883H0.75708V381.937H276.634V309.883Z"
              fill="white"
            />
          </svg>
        )}
        <Container className="mt-vertical-small">
          <div className={'gap-lg flex flex-col md:max-w-2/3 xl:max-w-1/2'}>
            <Base as="h1" anim="title" className="h1">
              {title}
            </Base>
            {text && (
              <Base className={'text-style-large'} anim="title" role="textbox">
                <Parse html={text} />
              </Base>
            )}
          </div>
        </Container>
      </div>
    </Section>
  )
}

export default memo(SectionHero)
